import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, FileText, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CourseViewer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchCourse() {
      try {
        const res = await axios.get(`${API}/courses/${courseId}`);
        setCourse(res.data);
      } catch {
        setError("Course not found");
      } finally {
        setLoading(false);
      }
    }
    fetchCourse();
  }, [courseId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F7F5F0" }}>
        <Loader2 size={24} className="animate-spin text-[#4A6741]" />
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#F7F5F0" }}>
        <div className="text-center">
          <p className="text-sm text-[#5C635A] mb-4">{error || "Course not found"}</p>
          <button onClick={() => navigate("/courses")} className="btn-pill-outline text-[11px]">
            <ArrowLeft size={12} /> Back to Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{course.title} | ALIGN• Courses</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: "#F7F5F0" }} data-testid="course-viewer">
        <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="text-[#5C635A] hover:text-[#4A6741] transition-colors" data-testid="viewer-back-button">
                <ArrowLeft size={18} />
              </button>
              <button onClick={() => navigate("/")} className="font-['Cormorant_Garamond'] text-2xl font-light tracking-tight text-[#1A1C19] hover:text-[#4A6741] transition-colors">
                ALIGN<span className="text-[#4A6741]">&bull;</span>
              </button>
            </div>
            <span className={`text-[10px] uppercase tracking-wider font-medium px-3 py-1 rounded-full ${course.published ? "bg-[#10b981]/10 text-[#10b981]" : "bg-[#EAE6DF] text-[#5C635A]"}`}>
              {course.published ? "Published" : "Draft"}
            </span>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            {/* Category & Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="text-[10px] uppercase tracking-wider font-medium px-3 py-1 rounded-full bg-[#4A6741]/10 text-[#4A6741]">
                {course.category === "allied-health" ? "Allied Health Professionals" : "Parents & Caregivers"}
              </span>
              {course.tags?.map((tag) => (
                <span key={tag} className="text-[10px] uppercase tracking-wider font-medium px-3 py-1 rounded-full bg-[#EAE6DF] text-[#5C635A]">
                  {tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light tracking-tight text-[#1A1C19] mb-4 leading-[1.1]">
              {course.title}
            </h1>

            {course.subtitle && (
              <p className="text-lg font-medium text-[#4A6741] mb-8">{course.subtitle}</p>
            )}

            <div className="section-divider mb-8" />

            {/* Description */}
            <p className="text-lg font-light text-[#5C635A] leading-relaxed mb-10">
              {course.description}
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              {course.link && (
                <a
                  href={course.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pill text-[11px] justify-center"
                  data-testid="course-launch-button"
                >
                  <ExternalLink size={14} />
                  Open Full Course
                </a>
              )}
              {course.pdf_review && (
                <a
                  href={course.pdf_review}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-pill-outline text-[11px] justify-center"
                  data-testid="course-pdf-button"
                >
                  <FileText size={14} />
                  Course Overview PDF
                </a>
              )}
            </div>

            {/* Back */}
            <div className="pt-8 border-t border-[#EAE6DF]">
              <button onClick={() => navigate("/courses")} className="text-xs text-[#5C635A] hover:text-[#4A6741] transition-colors inline-flex items-center gap-2">
                <ArrowLeft size={12} /> Back to All Courses
              </button>
            </div>
          </motion.div>
        </main>
      </div>
    </>
  );
}
